import { Typography } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { getMinQuantity } from 'components/Checkout/CartForm/utils'
import WaitingListModale from 'components/WaitingList/WaitingListModale'
import { Button, ButtonVariant } from 'components/common/Button'
import { Status } from 'components/common/Status/Status'
import { FrontRaceService } from 'domain/services/RaceService/FrontRaceService'
import { usePriceFormat } from 'hooks/usePriceFormat'
import { LiveEventRace } from 'services/api/database/EventService'
import {
  PriceValue,
  RaceCardResumeContainer,
  RaceCardResumePriceContainer,
  RaceCardResumeRaceCardFooter
} from './RaceCard.style'

interface ResumeRaceCardContentProps {
  isValidPrice: boolean
  finalPrice: { amount: number; currency: string }
  hasReduction: boolean
  finalReductionPrice: { amount: number; currency: string }
  ctaLink: string
  raceStatus: Status
  handleClick: () => void
  race: LiveEventRace
  ctaLabel: string
  isWaitingList: boolean
  isFeatured: boolean | null
}

export const ResumeRaceCardContent = ({
  isValidPrice,
  finalPrice,
  hasReduction,
  finalReductionPrice,
  ctaLink,
  raceStatus,
  handleClick,
  race,
  ctaLabel,
  isWaitingList,
  isFeatured
}: ResumeRaceCardContentProps) => {
  const { t } = useTranslation(['common', 'event', 'checkout'])
  const [openWaitingListModale, setOpenWaitingListModale] = useState(false)
  const { name, startDate, price } = race

  const { formatPriceDefault } = usePriceFormat()
  const isRaceActive = raceStatus === Status.ONLINE

  return (
    <>
      <RaceCardResumeContainer>
        <Typography variant="body1" fontWeight={600}>
          {name}
        </Typography>
        <RaceCardResumeRaceCardFooter
          $isValidPrice={isValidPrice}
          $largerMargin={!startDate}
        >
          {isValidPrice ? (
            <RaceCardResumePriceContainer
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-end"
            >
              <PriceValue variant="body2" $hasReduction={hasReduction}>
                {price === 0
                  ? t('free')
                  : formatPriceDefault(
                      finalPrice.amount,
                      finalPrice.currency,
                      getMinQuantity(race)
                    )}
              </PriceValue>
              {hasReduction && (
                <PriceValue variant="body2" $isRed>
                  {finalReductionPrice.amount === 0
                    ? t('free')
                    : formatPriceDefault(
                        finalReductionPrice.amount,
                        finalReductionPrice.currency,
                        getMinQuantity(race)
                      )}
                </PriceValue>
              )}
              {FrontRaceService.displayPerParticipantLabel(race) && (
                <Typography
                  whiteSpace="nowrap"
                  variant="bodySRegular"
                  color="label.subdued"
                >
                  {t('event:perParticipant')}
                </Typography>
              )}
            </RaceCardResumePriceContainer>
          ) : null}
        </RaceCardResumeRaceCardFooter>
        <RaceCardResumeRaceCardFooter
          $isValidPrice={isValidPrice}
          $largerMargin={!startDate}
        >
          {isWaitingList ? (
            <Button
              variant={ButtonVariant.Default}
              label={t('checkout:race.waitingList.beAlerted')}
              onClick={() => setOpenWaitingListModale(true)}
            />
          ) : (
            ctaLink && (
              <Button
                variant={
                  !isFeatured
                    ? ButtonVariant.Default
                    : !isRaceActive
                    ? ButtonVariant.Disabled
                    : ButtonVariant.Primary
                }
                label={ctaLabel}
                onClick={handleClick}
              />
            )
          )}
        </RaceCardResumeRaceCardFooter>
      </RaceCardResumeContainer>
      <WaitingListModale
        open={openWaitingListModale}
        onClose={() => setOpenWaitingListModale(false)}
        race={race}
      />
    </>
  )
}
